/**
 * @see https://umijs.org/docs/max/access#access
 * */
export const OPS_PERMISSION = {
  DASHBOARD: 'luole-admin.dashboard',
  OPS_OVERVIEW: 'luole-admin.ops.overview',
  ENTERPRISE_USERS: 'luole-admin.enterprise.users',
  ENTERPRISE_VERIFY: 'luole-admin.enterprise.verify',
  DEMO_RESERVATION: 'luole-admin.demoReservation',
  USER_FEEDBACK: 'luole-admin.userFeedback',
  FORUM_REGISTRATION: 'luole-admin.forumRegistration',
  JOB_FAIR_REGISTRATION: 'luole-admin.jobFairRegistration',
  JOB_FAIR: 'luole-admin.job-fair.management',
  DICTIONARY: 'luole-admin.global.dictionary',
  REGISTRATION_CHANNELS: 'luole-admin.registrationChannels',
  SYSTEM_ROLES: 'luole-admin.system.role-permissions',
  SYSTEM_MEMBERS: 'luole-admin.system.members',
} as const;

export const OPS_ADMIN_MENU_NAMES = new Set([
  OPS_PERMISSION.SYSTEM_ROLES,
  OPS_PERMISSION.SYSTEM_MEMBERS,
]);

type OpsMe = {
  role?: 'admin' | 'owner' | 'manager' | 'member';
  permissions?: string[];
  canEnterpriseVerify?: boolean;
};

const isPlatformAdmin = (currentUser?: API.CurrentUser) =>
  Boolean(currentUser?.isAdmin || currentUser?.type === 'admin');

const hasOpsPermission = (opsMe: OpsMe | null | undefined, code: string) =>
  Boolean(opsMe?.permissions?.includes(code));

const isOpsAdmin = (opsMe?: OpsMe | null) =>
  opsMe?.role === 'admin' ||
  hasOpsPermission(opsMe, OPS_PERMISSION.SYSTEM_ROLES);

export const canReviewEnterpriseCertification = (opsMe?: OpsMe | null) =>
  Boolean(opsMe?.canEnterpriseVerify) ||
  hasOpsPermission(opsMe, OPS_PERMISSION.ENTERPRISE_VERIFY) ||
  opsMe?.role === 'admin' ||
  opsMe?.role === 'manager';

export const isOpsMenuItemAllowed = (
  name: string,
  options?: {
    permissions?: string[];
    canOpsAdmin?: boolean;
    canEnterpriseVerify?: boolean;
  },
) => {
  if (Array.isArray(options?.permissions)) {
    return options.permissions.includes(name);
  }
  if (OPS_ADMIN_MENU_NAMES.has(name) && !options?.canOpsAdmin) {
    return false;
  }
  if (
    name === OPS_PERMISSION.ENTERPRISE_VERIFY &&
    !options?.canEnterpriseVerify
  ) {
    return false;
  }
  return true;
};

export default function access(
  initialState:
    | { currentUser?: API.CurrentUser; opsMe?: OpsMe | null }
    | undefined,
) {
  const { currentUser, opsMe } = initialState ?? {};
  const opsRolePending = opsMe === undefined;
  const allowPendingAdmin = opsRolePending && isPlatformAdmin(currentUser);
  const can = (code: string) =>
    allowPendingAdmin ||
    opsMe?.role === 'admin' ||
    hasOpsPermission(opsMe, code);
  const canOpsAdmin = isOpsAdmin(opsMe) || allowPendingAdmin;
  const canEnterpriseVerify =
    canReviewEnterpriseCertification(opsMe) || allowPendingAdmin;
  const canOps = Boolean(currentUser) || Boolean(opsMe?.role) || canOpsAdmin;
  return {
    canAdmin: Boolean(currentUser && currentUser.type === 'admin'),
    canOps,
    canOpsAdmin,
    canEnterpriseVerify,
    canOpsWorkbench: canOps,
    canOpsOverview: can(OPS_PERMISSION.OPS_OVERVIEW),
    canEnterpriseUsers: can(OPS_PERMISSION.ENTERPRISE_USERS),
    canDemoReservation: can(OPS_PERMISSION.DEMO_RESERVATION),
    canUserFeedback: can(OPS_PERMISSION.USER_FEEDBACK),
    canForumRegistration: can(OPS_PERMISSION.FORUM_REGISTRATION),
    canJobFairRegistration: can(OPS_PERMISSION.JOB_FAIR_REGISTRATION),
    canJobFairManagement: can(OPS_PERMISSION.JOB_FAIR),
    canDictionary: can(OPS_PERMISSION.DICTIONARY),
    canRegistrationChannels: can(OPS_PERMISSION.REGISTRATION_CHANNELS),
  };
}
